import { shouldDisplayAnyContact } from '@/modules/solutions/contact';
import { filterRecordsByActiveCriteriaInContextFilter } from '@/modules/solutions/solutionsFilters';
import {
  CRITERIA_CAMPUSES_SOME_IS_IN_KEY,
  CRITERIA_EDUCATIONAL_PROGRAM_STEPS_SOME_IS_IN_KEY,
  findByStudentSolutionEnhancedCriteriaRule,
  shouldDisplayCardDetails,
} from '@/modules/solutions/studentSolution';
import {
  Amplitude,
  LogOnChange,
} from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import size from 'lodash.size';
import React, {
  Fragment,
  useState,
} from 'react';

import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import { ContextFiltersSettings } from '@/modules/core/data/types/ContextFiltersSettings';
import { Asset } from '@/modules/core/data/types/Asset';
import { Campus } from '@/modules/core/data/types/Campus';
import { Contact } from '@/modules/core/data/types/Contact';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { LabeledLink } from '@/modules/core/data/types/LabeledLink';
import { Logo } from '@/modules/core/data/types/Logo';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { StudentSolutionContext } from '@/modules/core/data/types/StudentSolutionContext';
import SolutionCardDetails from './SolutionCardDetails';
import SolutionCardTeaser from './SolutionCardTeaser';
import SolutionCardToggleCollapseDetails from './SolutionCardToggleCollapseDetails';

type Props = {
  contextFiltersSettings: ContextFiltersSettings;
  studentSolution: StudentSolution;
  index: number;
  context: StudentSolutionContext;
  isReportDialogOpen: boolean;
  setIsReportDialogOpen: (isOpen: boolean) => void;
  setSelectedStudentSolutionToReport: (studentSolution: StudentSolution) => void;
}

const SolutionCard: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    contextFiltersSettings,
    studentSolution,
    index,
    context,
    isReportDialogOpen,
    setIsReportDialogOpen,
    setSelectedStudentSolutionToReport,
  }: Props = props;
  const theme: CustomerTheme = useTheme();
  const [isCardDetailsOpen, setIsCardDetailsOpen] = useState<boolean>(false);
  const logo: Logo = studentSolution?.logo;
  const loanMaxAmount: number = studentSolution?.loanMaxAmount;
  const loanInterestRate: number = studentSolution?.loanInterestRate;
  const expiresAt: string = studentSolution?.expiresAt;
  const contacts: Contact[] = studentSolution?.contactsBasicMatches;
  const hasAnyContactToDisplay = shouldDisplayAnyContact(contacts);
  const documents: Asset[] = studentSolution?.documents;
  const links: LabeledLink[] = studentSolution?.links;
  const description: string = studentSolution?.description;
  const eligibilityConditionsDescription: string = studentSolution?.eligibilityConditionsDescription;
  const benefitsDescription: string = studentSolution?.benefitsDescription;
  const categories: StudentSolutionsCategory[] = studentSolution?.categories;
  const hasSolutionCardDetails = shouldDisplayCardDetails(studentSolution);
  const campuses: Array<Campus> = filterRecordsByActiveCriteriaInContextFilter(context, findByStudentSolutionEnhancedCriteriaRule(studentSolution, CRITERIA_CAMPUSES_SOME_IS_IN_KEY, []), 'Campus');
  const educationalProgramSteps: EducationalProgramStep[] = filterRecordsByActiveCriteriaInContextFilter(context, findByStudentSolutionEnhancedCriteriaRule(studentSolution, CRITERIA_EDUCATIONAL_PROGRAM_STEPS_SOME_IS_IN_KEY, []), 'EducationalProgramStep') as unknown as EducationalProgramStep[];

  return (
    <Amplitude
      eventProperties={(inheritedProps): GenericObject => ({
        ...inheritedProps,
        solution: {
          ref: studentSolution.ref,
          hasLink: !!size(links),
          hasDocument: !!size(documents),
          hasContact: hasAnyContactToDisplay,
          rank: index + 1,
        },
      })}
    >
      {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
        <Fragment key={studentSolution?.id}>
          <LogOnChange
            value={isCardDetailsOpen}
            eventType={'solutions-details-displayed'}
            eventProperties={{
              selection: {
                isOpen: isCardDetailsOpen,
              },
            }}
          />

          <div
            css={css`
              > div {
                display: flex;
                align-items: center;

                @media (min-width: 992px) {
                  display: flex !important; // Overrides bootstrap to force using flex with desktop to center vertically elements
                }
              }

              .card-teaser {
                position: relative;
                margin-top: 15px;
                padding-top: 20px;
                padding-bottom: 20px;
                border-radius: 15px;
                background-color: #FFFFFF;
                transition: 0.5s ease-in-out;

                &:hover {
                  box-shadow: 3px 3px 24px -4px rgba(0, 0, 0, 0.50);

                  // Animate the description arrow related to the current "teaset" (placed within "toggle-collapse-row" which is after "teaset")
                  & + .toggle-collapse-row {
                    .description-arrow {
                      animation-iteration-count: infinite;
                    }
                  }
                }
              }

              .additional-description-row {
                p {
                  font-weight: 300;
                  font-size: 15px;
                  line-height: 16.5px;
                }
              }

              .column-divider {
                border-left: 1px solid #D0D0D0;
                margin-left: -1px; // Compensates border size to avoid line break
                margin-top: 5px;
                margin-bottom: 5px;
                min-height: 20px;
              }

              .description-arrow-container {
                &:hover {
                  .description-arrow {
                    animation-iteration-count: infinite;
                  }
                }
              }

              .description-arrow {
                position: absolute;
                right: 10px;
                top: 15px;
                color: ${theme.primaryColor};
              }
            `}
          >
            <SolutionCardTeaser
              index={index}
              hasSolutionCardDetails={hasSolutionCardDetails}
              links={links}
              campuses={campuses}
              contacts={contacts}
              context={context}
              contextFiltersSettings={contextFiltersSettings}
              isCardDetailsOpen={isCardDetailsOpen}
              educationalProgramSteps={educationalProgramSteps}
              hasAnyContactToDisplay={hasAnyContactToDisplay}
              logo={logo}
              setIsCardDetailsOpen={setIsCardDetailsOpen}
              studentSolution={studentSolution}
            />
            {
              hasSolutionCardDetails && (
                <Fragment>
                  <SolutionCardToggleCollapseDetails
                    isCardDetailsOpen={isCardDetailsOpen}
                    setIsCardDetailsOpen={setIsCardDetailsOpen}
                  />
                  {
                    isCardDetailsOpen && (
                      <SolutionCardDetails
                        documents={documents}
                        links={links}
                        benefitsDescription={benefitsDescription}
                        campuses={campuses}
                        contacts={contacts}
                        context={context}
                        contextFiltersSettings={contextFiltersSettings}
                        eligibilityConditionsDescription={eligibilityConditionsDescription}
                        description={description}
                        educationalProgramSteps={educationalProgramSteps}
                        expiresAt={expiresAt}
                        hasAnyContactToDisplay={hasAnyContactToDisplay}
                        loanInterestRate={loanInterestRate}
                        loanMaxAmount={loanMaxAmount}
                        studentSolution={studentSolution}
                        categories={categories}
                        isReportDialogOpen={isReportDialogOpen}
                        setIsReportDialogOpen={setIsReportDialogOpen}
                        setSelectedStudentSolutionToReport={setSelectedStudentSolutionToReport}
                      />
                    )
                  }
                </Fragment>
              )
            }
          </div>
        </Fragment>
      )}
    </Amplitude>
  );
};

export default SolutionCard;
